import { Link } from '@tanstack/react-router';
import { Plus, Anchor, Timer, Gauge, Star, MapPin, ArrowUpRight } from 'lucide-react';
import { useDiveLogs, useDiveLogsMap } from '@/data/dive-logs';
import { useAuthStore } from '@/stores/auth';
import { MapView } from '@/components/map/MapView';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Spinner } from '@/components/ui/spinner';
import { formatGermanDate } from '@/lib/format';
import { useDocumentTitle } from '@/lib/useDocumentTitle';
import { StaticMap } from '@/components/map/StaticMap';

function Stat({ label, value, hint, icon: Icon, accent }: {
  label: string; value: string | number; hint?: string;
  icon: React.ComponentType<{ className?: string }>;
  accent: 'ocean' | 'sand' | 'coral';
}) {
  const tone = {
    ocean: 'from-ocean-700 to-ocean-500 text-white',
    sand:  'from-sand-300 to-sand-200 text-ocean-900',
    coral: 'from-coral-500 to-coral-600 text-white',
  }[accent];
  return (
    <Card className="relative overflow-hidden">
      <div className={`absolute inset-x-0 top-0 h-1 bg-gradient-to-r ${tone}`} />
      <CardContent className="pt-6">
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="text-xs font-medium uppercase tracking-wider text-[var(--muted-foreground)]">{label}</p>
            <p className="mt-1 text-3xl font-semibold tracking-tight">{value}</p>
            {hint && <p className="mt-1 text-xs text-[var(--muted-foreground)]">{hint}</p>}
          </div>
          <div className={`rounded-lg bg-gradient-to-br ${tone} p-2.5 shadow-sm`}>
            <Icon className="h-5 w-5" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export function DashboardPage() {
  useDocumentTitle('Dashboard');
  const user = useAuthStore((s) => s.user);
  const { data: listData }   = useDiveLogs({ limit: 5 });
  const { data: mapData, isLoading: mapLoading } = useDiveLogsMap();

  const recent       = listData?.items ?? [];
  const total        = listData?.total ?? 0;
  const totalLogs    = mapData?.items ?? [];
  const totalMinutes = totalLogs.reduce((sum, l) => sum + (l.diveTimeMinutes ?? 0), 0);
  const totalHours   = (totalMinutes / 60).toFixed(1);
  const deepestDive  = totalLogs.reduce((max, l) => Math.max(max, l.maxDepth ?? 0), 0);
  const ratings      = totalLogs.map((l) => l.rating).filter((r): r is number => r != null);
  const avgRating    = ratings.length > 0 ? (ratings.reduce((s, r) => s + r, 0) / ratings.length).toFixed(1) : '–';

  const points = totalLogs.map((l) => ({
    id:    l.id,
    lat:   l.site.lat,
    lng:   l.site.lon,
    href:  `/logs/${l.id}`,
    tooltip: {
      title:    l.title,
      subtitle: l.site.title,
      badge:    l.userIndex ? `#${l.userIndex}` : undefined,
      metrics:  [
        { icon: 'date'     as const, label: 'Datum',  value: formatGermanDate(l.diveDate) },
        { icon: 'depth'    as const, label: 'Tiefe',  value: l.maxDepth ? `${l.maxDepth} m` : '–' },
        { icon: 'duration' as const, label: 'Dauer',  value: l.diveTimeMinutes ? `${l.diveTimeMinutes} min` : '–' },
      ],
    },
  }));

  return (
    <div className="space-y-6 p-4 sm:p-6 md:p-8">
      <header className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-ocean-700 via-ocean-600 to-ocean-500 p-4 sm:p-6 md:p-8 text-white shadow-lg">
        <div
          aria-hidden
          className="pointer-events-none absolute -right-16 -top-16 h-64 w-64 rounded-full bg-coral-500/20 blur-3xl"
        />
        <div className="relative flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="text-sm text-ocean-100">{greeting()}, {user?.firstname ?? 'Taucher'}.</p>
            <h1 className="mt-1 text-3xl font-semibold tracking-tight md:text-4xl">Dein Tauchlogbuch</h1>
            <p className="mt-2 max-w-lg text-sm text-ocean-100">
              {total === 0
                ? 'Logge deinen ersten Tauchgang und sieh ihn auf der Karte.'
                : `${total} Tauchgang${total === 1 ? '' : 'gänge'} geloggt, ${totalHours} h unter Wasser.`}
            </p>
          </div>
          <Link
            to="/logs/new"
            className="inline-flex h-11 items-center gap-2 rounded-md bg-white/10 px-5 text-sm font-medium text-white shadow-sm ring-1 ring-white/30 backdrop-blur transition hover:bg-white/20"
          >
            <Plus className="h-4 w-4" /> Neuer Tauchgang
          </Link>
        </div>
      </header>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Stat label="Tauchgänge"   value={total}                hint={`davon ${points.length} mit Position`} icon={Anchor} accent="ocean" />
        <Stat label="Stunden u.W." value={totalHours}           hint={`${totalMinutes} Minuten gesamt`}      icon={Timer}  accent="ocean" />
        <Stat label="Tiefster"     value={deepestDive ? `${deepestDive} m` : '–'} icon={Gauge}              accent="sand" />
        <Stat label="Ø Bewertung"  value={avgRating}            hint={`aus ${ratings.length} Bewertungen`}   icon={Star}   accent="coral" />
      </div>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0">
          <CardTitle className="flex items-center gap-2"><MapPin className="h-4 w-4 text-ocean-700" /> Karte deiner Tauchgänge</CardTitle>
          <span className="text-xs text-[var(--muted-foreground)]">
            {points.length} {points.length === 1 ? 'Pin' : 'Pins'}
          </span>
        </CardHeader>
        <CardContent>
          {mapLoading ? (
            <div className="grid place-items-center" style={{ height: 360 }}><Spinner /></div>
          ) : points.length === 0 ? (
            <div className="grid place-items-center rounded-xl bg-ocean-50 text-sm text-ocean-700 dark:bg-ocean-900/30 dark:text-ocean-200" style={{ height: 360 }}>
              Noch keine Tauchgänge mit Position.
            </div>
          ) : (
            <MapView points={points} height={420} />
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0">
          <CardTitle>Letzte Tauchgänge</CardTitle>
          {total > 5 && (
            <Link to="/logs" className="inline-flex items-center gap-1 text-xs font-medium text-ocean-700 hover:underline">
              Alle anzeigen <ArrowUpRight className="h-3.5 w-3.5" />
            </Link>
          )}
        </CardHeader>
        <CardContent>
          {recent.length === 0 ? (
            <p className="py-8 text-center text-sm text-[var(--muted-foreground)]">
              Noch keine Tauchgänge. <Link to="/logs/new" className="text-ocean-700 hover:underline">Jetzt ersten anlegen</Link>.
            </p>
          ) : (
            <ul className="space-y-3">
              {recent.map((log) => {
                const hasCoords = log.diveSite?.geoLatitude != null && log.diveSite?.geoLongitude != null;
                return (
                  <li key={log.id}>
                    <Link
                      to="/logs/$id"
                      params={{ id: String(log.id) }}
                      className="group flex items-stretch gap-3 overflow-hidden rounded-xl border border-[var(--border)] bg-[var(--card)] transition hover:border-ocean-200 hover:shadow-md sm:gap-4"
                    >
                      <div className="relative h-20 w-24 shrink-0 overflow-hidden bg-gradient-to-br from-ocean-700 to-ocean-900 sm:h-24 sm:w-32">
                        {hasCoords && (
                          <StaticMap
                            lat={log.diveSite!.geoLatitude!}
                            lng={log.diveSite!.geoLongitude!}
                            zoom={9}
                            width={128}
                            height={96}
                          />
                        )}
                        <div className="absolute inset-0 bg-gradient-to-t from-ocean-950/40 to-transparent" />
                        {log.userIndex && (
                          <span className="absolute left-1.5 top-1.5 rounded-full bg-white/85 px-1.5 py-0.5 text-[10px] font-semibold text-ocean-800 shadow-sm backdrop-blur">
                            #{log.userIndex}
                          </span>
                        )}
                      </div>

                      <div className="flex min-w-0 flex-1 flex-col justify-center py-2 pr-3">
                        <div className="flex items-start justify-between gap-2">
                          <h3 className="truncate text-sm font-semibold leading-tight transition group-hover:text-ocean-700 sm:text-base">
                            {log.title}
                          </h3>
                          {log.rating && (
                            <span className="shrink-0 text-xs text-coral-500" aria-label={`${log.rating} Sterne`}>
                              {'★'.repeat(log.rating)}
                            </span>
                          )}
                        </div>
                        <p className="mt-0.5 truncate text-xs text-[var(--muted-foreground)]">
                          {formatGermanDate(log.diveDate)} · {log.diveSite?.label ?? 'Unbekannter Ort'}
                        </p>
                        <div className="mt-2 flex flex-wrap gap-1.5">
                          <span className="inline-flex items-center gap-1 rounded-full bg-ocean-50 px-2 py-0.5 text-[11px] font-medium text-ocean-700 dark:bg-ocean-900/40 dark:text-ocean-200">
                            <Gauge className="h-3 w-3" />
                            {log.maxDepth} m
                          </span>
                          <span className="inline-flex items-center gap-1 rounded-full bg-ocean-50 px-2 py-0.5 text-[11px] font-medium text-ocean-700 dark:bg-ocean-900/40 dark:text-ocean-200">
                            <Timer className="h-3 w-3" />
                            {log.diveTimeMinutes} min
                          </span>
                          {log.diveSite?.siteType && (
                            <span className="inline-flex rounded-full bg-sand-100 px-2 py-0.5 text-[11px] font-medium uppercase tracking-wide text-ocean-800 dark:bg-sand-200/20 dark:text-sand-100">
                              {log.diveSite.siteType.replace('_', ' ')}
                            </span>
                          )}
                        </div>
                      </div>
                    </Link>
                  </li>
                );
              })}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function greeting(): string {
  const h = new Date().getHours();
  if (h < 5)  return 'Gute Nacht';
  if (h < 11) return 'Guten Morgen';
  if (h < 18) return 'Hallo';
  return 'Guten Abend';
}

